import http from './http';

export interface AssistantPrefs {
  enabled: boolean;
  assistant_name: string;
  personality_mode: string;
  avatar_id: string;
  custom_avatar_path: string | null;
  widget_position: string;
  proactive_suggestions: boolean;
  suggestion_interval_minutes: number;
  sound_enabled: boolean;
  animation_enabled: boolean;
  haptic_enabled: boolean;
  voice_activation_enabled: boolean;
  voice_mode_enabled: boolean;
  voice_auto_listen_enabled: boolean;
  voice_transcript_enabled: boolean;
  second_mind_enabled: boolean;
  /** Opt-in speech-bubble mode (owner directive 2026-10-10). */
  bubble_mode_enabled: boolean;
  /** Per-agent avatar overrides for the three assistant lines (null = main). */
  avatar_id_ricardo: string | null;
  avatar_id_henry: string | null;
  vision_credential_profile_id: string | null;
  vision_model: string | null;
  file_analysis_credential_profile_id: string | null;
  file_analysis_model: string | null;
  image_credential_profile_id: string | null;
  image_model: string | null;
  video_credential_profile_id: string | null;
  video_model: string | null;
  /** True when a TencentDB memory binding exists (key itself is never exposed). */
  memory_proxy_bound?: boolean;
  /** The model this user's own long-term memory uses (owner directive 2026-09-21). */
  memory_model?: string | null;
  memory_provider?: string | null;
  memory_enabled?: boolean;
  /** Published by the backend so this list can never drift from what is valid. */
  memory_model_choices?: string[];
  memory_default_model?: string;
  memory_instance?: string;
}

export interface AssistantAvatar {
  id: string;
  name: string;
  description: string;
  path: string;
  image?: boolean;
  humanoid?: boolean;
}

export interface AssistantMessage {
  id: number;
  type: string;
  title: string | null;
  body: string;
  action_url: string | null;
  dismissed: boolean;
  snoozed_until: string | null;
  created_at: string;
}

export const assistantApi = {
  getPrefs: () => http.get<AssistantPrefs>('/assistant/prefs').then(r => r.data),
  updatePrefs: (prefs: Partial<AssistantPrefs>) => http.put('/assistant/prefs', prefs).then(r => r.data),
  getMessages: () => http.get<AssistantMessage[]>('/assistant/messages').then(r => r.data),
  dismissMessage: (id: number) => http.post(`/assistant/messages/${id}/dismiss`).then(r => r.data),
  snoozeMessage: (id: number, minutes: number) => http.post(`/assistant/messages/${id}/snooze`, { minutes }).then(r => r.data),
  chat: (message: string) => http.post<{ response: string; personality: string; error?: boolean }>('/assistant/chat', { message }).then(r => r.data),
  greet: (bubble = false) => http.post<{ message: string | null; assistant_name?: string }>(`/assistant/greet${bubble ? '?bubble=true' : ''}`, {}).then(r => r.data),
  // App-open contact check (owner 2026-10-12): does an agent have an unanswered
  // ask waiting? If so the backend raises ONE ring — the incoming-call screen
  // takes over instead of the chat quietly opening with a greeting.
  checkIn: () => http.post<{ rang: boolean; waiting?: number; title?: string | null; question?: string | null; severity?: string | null }>('/assistant/check-in', {}).then(r => r.data),
  getAvatars: () => http.get<AssistantAvatar[]>('/assistant/avatars').then(r => r.data),
  uploadAvatar: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return http.post('/assistant/avatar/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
};
